(() => {
  const ready = (fn) => document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn();
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('motion-off');
  const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  ready(() => {
    const header = document.querySelector('[data-header]');
    const onScroll = () => header && header.classList.toggle('is-shrunk', window.scrollY > 38);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const toggle = document.querySelector('[data-nav-toggle]');
    const mobile = document.querySelector('[data-mobile-nav]');
    if (toggle && mobile) {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        mobile.hidden = open;
      });
    }

    const reveal = (el) => el.classList.add('in');
    const loadEls = document.querySelectorAll('[data-load]');
    requestAnimationFrame(() => requestAnimationFrame(() => loadEls.forEach(reveal)));
    if (reduce || !('IntersectionObserver' in window)) {
      document.querySelectorAll('.rv').forEach(reveal);
    } else {
      const io = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.16, rootMargin: '0px 0px -5% 0px' });
      document.querySelectorAll('.rv:not([data-load])').forEach((el) => io.observe(el));
    }

    const relay = document.querySelector('[data-relay]');
    if (relay) requestAnimationFrame(() => requestAnimationFrame(() => relay.classList.add('is-live')));

    document.querySelectorAll('[data-faq-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('[data-faq]');
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        item.classList.toggle('is-open', !open);
      });
    });

    document.querySelectorAll('[data-stage-slider]').forEach((slider) => {
      const track = slider.querySelector('[data-stage-track]');
      const stages = Array.from(slider.querySelectorAll('[data-stage]'));
      const prev = slider.querySelector('[data-stage-prev]');
      const next = slider.querySelector('[data-stage-next]');
      const dots = slider.querySelector('[data-stage-dots]');
      if (!track || !stages.length || !prev || !next || !dots) return;
      let index = 0;
      stages.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to step ${i + 1}`);
        dot.addEventListener('click', () => go(i));
        dots.appendChild(dot);
      });
      const update = () => {
        Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        prev.disabled = index === 0;
        next.disabled = index === stages.length - 1;
      };
      const go = (nextIndex) => {
        index = Math.max(0, Math.min(stages.length - 1, nextIndex));
        const left = index * track.clientWidth;
        if (typeof track.scrollTo === 'function') track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
        else track.scrollLeft = left;
        update();
      };
      prev.addEventListener('click', () => go(index - 1));
      next.addEventListener('click', () => go(index + 1));
      track.addEventListener('scroll', () => {
        const width = track.clientWidth || 1;
        index = Math.max(0, Math.min(stages.length - 1, Math.round(track.scrollLeft / width)));
        update();
      }, { passive: true });
      update();
    });

    if (!reduce) {
      document.querySelectorAll('[data-magnetic]').forEach((el) => {
        el.addEventListener('pointermove', (event) => {
          const r = el.getBoundingClientRect();
          el.style.setProperty('--mx', `${(event.clientX - r.left - r.width / 2) * 0.12}px`);
          el.style.setProperty('--my', `${(event.clientY - r.top - r.height / 2) * 0.12}px`);
        });
        el.addEventListener('pointerleave', () => {
          el.style.setProperty('--mx', '0px');
          el.style.setProperty('--my', '0px');
        });
      });

      document.querySelectorAll('[data-tilt]').forEach((el) => {
        el.addEventListener('pointermove', (event) => {
          const r = el.getBoundingClientRect();
          const x = (event.clientX - r.left) / r.width - 0.5;
          const y = (event.clientY - r.top) / r.height - 0.5;
          el.style.setProperty('--ry', `${x * 7}deg`);
          el.style.setProperty('--rx', `${y * -7}deg`);
          el.style.setProperty('--tz', '4px');
        });
        el.addEventListener('pointerleave', () => {
          el.style.setProperty('--ry', '0deg');
          el.style.setProperty('--rx', '0deg');
          el.style.setProperty('--tz', '0px');
        });
      });

      document.querySelectorAll('[data-scramble-hover]').forEach((el) => {
        const label = el.querySelector('.btn__label') || el;
        const original = label.textContent.trim();
        let timer;
        el.addEventListener('mouseenter', () => {
          clearInterval(timer);
          let frame = 0;
          timer = setInterval(() => {
            label.textContent = original.split('').map((char, i) => {
              if (char === ' ' || i < frame / 2) return char;
              return randomChars[Math.floor(Math.random() * randomChars.length)];
            }).join('');
            frame += 1;
            if (frame > original.length * 2) {
              clearInterval(timer);
              label.textContent = original;
            }
          }, 28);
        });
        el.addEventListener('mouseleave', () => {
          clearInterval(timer);
          label.textContent = original;
        });
      });
    }

    const article = document.querySelector('[data-article]');
    const progress = document.querySelector('[data-reading-progress]');
    if (article && progress) {
      const updateProgress = () => {
        const rect = article.getBoundingClientRect();
        const total = Math.max(1, article.offsetHeight - window.innerHeight);
        const passed = Math.min(total, Math.max(0, -rect.top));
        progress.style.width = `${(passed / total) * 100}%`;
      };
      updateProgress();
      window.addEventListener('scroll', updateProgress, { passive: true });
    }

    const body = document.querySelector('[data-article-body]');
    const toc = document.querySelector('[data-toc-list]');
    if (body && toc) {
      const headings = Array.from(body.querySelectorAll('h2'));
      headings.forEach((heading, i) => {
        if (!heading.id) heading.id = `section-${i + 1}`;
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent;
        li.appendChild(link);
        toc.appendChild(li);
      });
      if (!headings.length) {
        const tocBox = toc.closest('[data-toc]');
        if (tocBox) tocBox.hidden = true;
      } else if ('IntersectionObserver' in window) {
        const links = Array.from(toc.querySelectorAll('a'));
        const headingIo = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              links.forEach((link) => link.classList.toggle('is-active', link.getAttribute('href') === `#${entry.target.id}`));
            }
          });
        }, { rootMargin: '-20% 0px -65% 0px' });
        headings.forEach((heading) => headingIo.observe(heading));
      }
    }
  });
})();
